import _every from 'lodash-es/every';
import _floor from 'lodash-es/floor';
import _last from 'lodash-es/last';
import { Row } from './types';
import { EDTEvent, EDTPDFSettings } from '@teetsh/app/src/types/edt';
import { BREAK_EVENT } from '../../constants';
import { timeBetweenSlices } from '@teetsh/app/src/utils/slices';
import { Classroom } from '@teetsh/app/src/types/classroom';
import _capitalize from 'lodash-es/capitalize';
import _flatten from 'lodash-es/flatten';
import { getLCMOfEvents } from '@teetsh/app/src/containers/printUtils/getLCMOfEvents';
import { format } from '@teetsh/app/src/utils/time/format';
import setDay from '@teetsh/app/src/utils/time/setDay';
import { getMutualWorkingDays } from '@teetsh/app/src/containers/utils';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { UserLocale } from '@teetsh/app/src/types/user';
import getClassroomsAvailableForThatDay from '@teetsh/app/src/components/EDT/getClassroomsAvailableForThatDay';

const BIG_BREAK_SLICE_MINUTE_TRESHOLD = 30;

const getRowHeight = (
  disableRelativeSlices: boolean,
  start: string,
  end: string,
  nbOfSlices: number,
  totalHeightInPixel: number,
  totalLengthInMinutes: number,
  isBreakSlice: boolean,
  smallBreak: boolean,
  heightInPixelOfBreaks: number,
) => {
  if (disableRelativeSlices) {
    return totalHeightInPixel / nbOfSlices;
  }
  const sliceLengthInMinute = timeBetweenSlices(start, end);
  const height =
    (totalHeightInPixel * sliceLengthInMinute) / totalLengthInMinutes;

  return isBreakSlice &&
    smallBreak &&
    sliceLengthInMinute > BIG_BREAK_SLICE_MINUTE_TRESHOLD
    ? heightInPixelOfBreaks
    : height;
};

function computeCellHeight(
  rows: Row[],
  currentRowIndex: number,
  rowSpan: number,
) {
  const currentRowHeight = rows[currentRowIndex]?.height || 0;
  if (rowSpan === 1) {
    return currentRowHeight;
  }
  let height = currentRowHeight;
  let index = currentRowIndex;
  Array.from({ length: rowSpan - 1 }).forEach(() => {
    height += rows[index + 1]?.height || 0;
    index++;
  });
  return height;
}

const MIN_ROW_HEIGHT = 12;

export function markVisuallyBottomRightCell(rows: Row[]): Row[] {
  // CSS :last-child selectors don't work correctly when cells have rowspan
  // because a cell in the DOM's last row might not be the visual bottom-right cell.
  // For example, a cell from row 2 with rowspan=3 could extend to row 4,
  // making it the visual bottom-right even though it's not in the last <tr>.
  // This function identifies which cell actually appears in the bottom-right
  // corner visually, so we can apply rounded corners correctly.

  const lastRowIndex = rows.length - 1;
  if (lastRowIndex < 0) return rows;

  // Track which cells visually appear in the last row
  let visuallyBottomRightRowIndex = -1;
  let visuallyBottomRightCellIndex = -1;

  // Check all rows to find cells that extend to the last row
  rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell, cellIndex) => {
      // Check if this cell extends to the last row
      const cellEndsAtRow = rowIndex + cell.rowspan - 1;
      if (cellEndsAtRow === lastRowIndex) {
        // This cell visually appears in the last row
        // Keep the rightmost one (highest cellIndex)
        if (
          visuallyBottomRightRowIndex === -1 ||
          cellIndex > visuallyBottomRightCellIndex
        ) {
          visuallyBottomRightRowIndex = rowIndex;
          visuallyBottomRightCellIndex = cellIndex;
        }
      }
    });
  });

  // Return a new array with the marked cell
  if (
    visuallyBottomRightRowIndex !== -1 &&
    visuallyBottomRightCellIndex !== -1
  ) {
    return rows.map((row, rowIndex) => {
      if (rowIndex !== visuallyBottomRightRowIndex) return row;

      return {
        ...row,
        cells: row.cells.map((cell, cellIndex) => {
          if (cellIndex !== visuallyBottomRightCellIndex) return cell;

          return {
            ...cell,
            isVisuallyBottomRight: true,
          };
        }),
      };
    });
  }

  return rows;
}

// Bon en gros...
// On calcul la taille des rows en fonction de l'espace qu'on a de dispo (maxHeight)
// en fonction des settings on veut que toutes les rows aient la même hauteur
// ou bien proportionnelle à la longueur de la slice
// par contre on veut pas qu'une row soit trop petite même si elle dure 1min
// d'où le MIN_ROW_HEIGHT
// Sauf, que comme on a des row span, on peut pas se permettre de mettre seulement une taille aux tr
// il faut aussi sizer les cellules (td), d'où le caclcul qui calcul la taille de la cellule en fonction de la
// taille des rows (et donc si rowsspan il faut aller chercher les tr suivants).
// Bref pas super simple cette histoire, mais pour le moment j'ai pas mieux (swear to god)
export const computeHeights = (
  rows: Row[],
  maxHeight: number,
  settings: EDTPDFSettings,
): Row[] => {
  if ((rows || []).length === 0) {
    return rows;
  }
  const bigBreakSlices = settings.smallBreak
    ? rows.filter(row => {
        const events = row.cells.map(c => c.event).filter(event => event);
        const isBreakSlice = _every(events, e => e && e.type === BREAK_EVENT);

        return (
          isBreakSlice &&
          timeBetweenSlices(row.start, row.end) >
            BIG_BREAK_SLICE_MINUTE_TRESHOLD
        );
      })
    : [];

  const lengthOfBigBreakSlices = bigBreakSlices.reduce(
    (acc, num) => acc + timeBetweenSlices(num.start, num.end),
    0,
  );
  const totalLengthInMinutes = timeBetweenSlices(
    rows[0].start,
    (_last(rows) as any).end,
  );
  const totalLengthInMinutesMinusBreaks =
    totalLengthInMinutes - lengthOfBigBreakSlices;

  const totalHeightInPixel = maxHeight - rows.length;

  const oneMinutePixel = totalHeightInPixel / totalLengthInMinutes;
  const heightInPixelOfBreaks =
    bigBreakSlices.length * BIG_BREAK_SLICE_MINUTE_TRESHOLD * oneMinutePixel;

  // Première passe : calculer les hauteurs initiales et identifier les lignes trop petites
  const initialHeights = rows.map(row => {
    const events = row.cells.map(c => c.event).filter(event => event);
    const isBreakSlice = _every(events, e => e && e.type === BREAK_EVENT);

    const rawHeight = getRowHeight(
      settings.disableRelativeSlices,
      row.start || '',
      row.end || '',
      rows.length,
      maxHeight - rows.length,
      totalLengthInMinutesMinusBreaks,
      isBreakSlice,
      settings.smallBreak,
      heightInPixelOfBreaks / bigBreakSlices.length,
    );

    return {
      row,
      rawHeight,
      needsMinHeight: rawHeight < MIN_ROW_HEIGHT,
    };
  });

  // Calculer l'espace nécessaire pour les lignes minimales
  const rowsNeedingMinHeight = initialHeights.filter(h => h.needsMinHeight);
  const totalMinHeightNeeded = rowsNeedingMinHeight.length * MIN_ROW_HEIGHT;

  // Calculer l'espace restant pour les autres lignes
  const remainingRows = initialHeights.filter(h => !h.needsMinHeight);
  const originalTotalHeight = remainingRows.reduce(
    (sum, h) => sum + h.rawHeight,
    0,
  );
  const remainingHeight = maxHeight - totalMinHeightNeeded - rows.length;

  // Facteur d'ajustement pour les lignes non minimales
  const adjustmentFactor = remainingHeight / originalTotalHeight;

  // Créer d'abord un tableau avec juste les hauteurs de ligne
  const rowsWithHeights = rows.map((row, index) => {
    const initialCalc = initialHeights[index];
    const finalHeight = initialCalc.needsMinHeight
      ? MIN_ROW_HEIGHT
      : Math.floor(initialCalc.rawHeight * adjustmentFactor);

    return {
      ...row,
      height: finalHeight,
    };
  });

  // Map cells with their computed heights
  const rowsWithCellHeights = rowsWithHeights.map((row, currentRowIndex) => ({
    ...row,
    cells: row.cells.map(cell => ({
      ...cell,
      height: computeCellHeight(rowsWithHeights, currentRowIndex, cell.rowspan),
    })),
  }));

  // Mark the visually bottom-right cell
  return markVisuallyBottomRightCell(rowsWithCellHeights);
};

function getCells(
  days: number[],
  classrooms: Classroom[],
  classroomsOrg: 'split' | 'unified',
  lcm: number,
) {
  const arrayOfCells = days.map(day => {
    const classroomsHappeningThatDay = getClassroomsAvailableForThatDay(
      classrooms,
      day,
    );
    const classroomsToDisplay =
      classroomsOrg === 'unified'
        ? [
            {
              name: classroomsHappeningThatDay.map(c => c.name).join('-'),
            },
          ]
        : classroomsHappeningThatDay;

    const colSpan = _floor(lcm / classroomsToDisplay.length);

    return classroomsToDisplay.map(classroom => ({
      title: classroom.name,
      rowspan: 1,
      // default height, should be overwritten by computeHeights
      height: 1,
      colspan: colSpan,
    }));
  });
  return _flatten(arrayOfCells);
}

export function computeHeaderRows(
  events: EDTEvent[],
  days: number[],
  classrooms: Classroom[],
  classroomsOrg: ClassroomsOrg,
  locale: UserLocale,
): Row[] {
  const lcm = getLCMOfEvents(events, classrooms, days, classroomsOrg);

  return [
    {
      title: '',
      // default height, should be overwritten by computeHeights
      height: 1,
      cells: days.map(day => ({
        title: _capitalize(
          format(
            setDay(
              new Date(),
              day,
              getMutualWorkingDays(classrooms).includes(-1),
            ),
            locale,
            'EEEE',
          ),
        ),
        colspan: lcm,
        rowspan: 1,
        // default height, should be overwritten by computeHeights
        height: 1,
      })),
    },
    {
      title: '',
      // default height, should be overwritten by computeHeights
      height: 1,
      cells: getCells(days, classrooms, classroomsOrg, lcm),
    },
  ];
}
